const STAGING_HOSTS = [
  'wlqa.testingmonkey.com',
  'automaker-gliding-pushover.ngrok-free.dev',
];

export const IS_STAGING = STAGING_HOSTS.some(host =>
  process.env.REACT_APP_API_BASE_URL?.includes(host),
);

export const PUBLIC_PATHS = {
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASS: '/forgot-password',
  INVITED_SIGNUP: '/invited-signup',
  JOIN_INVITED_USER: '/join-invited-user',
  OTP_SCREEN: '/verify-otp',
  ORGANIZATION_ACCOUNT_SETUP: '/organization-account-setup',
  RESET_PASSWORD: '/reset-password',
  UNAUTHORIZED: '/unauthorized',
} as const;

export const PRIVATE_PATHS = {
  // Core/Shared paths
  CORE: {
    DASHBOARD: '/:orgSlug/dashboard',
    ONBOARDING_SUCCESS: '/:orgSlug/onboarding-success',
    EMPLOYEE_ONBOARDING: '/:orgSlug/account-setup/employee-onboarding',
    MY_PROFILE: '/:orgSlug/hrms/my-profile',
    HANDBOOK: '/:orgSlug/hrms/handbook',
    RESIGNATION: '/:orgSlug/hrms/support/resignation',
    NOTIFICATIONS: '/:orgSlug/notifications',
    SETTINGS: {
      MANAGE_USERS: '/:orgSlug/settings/users',
      USER_PROFILE: '/:orgSlug/settings/user/profile',
      ORGANIZATION: '/:orgSlug/settings/organization',
    },
  },

  // HRMS Module paths
  HRMS: {
    DASHBOARD: '/:orgSlug/hrms/dashboard',
    NOTIFICATIONS: '/:orgSlug/hrms/notifications',
    EMPLOYEES: {
      VIEW_ALL: '/:orgSlug/hrms/employees',
      DETAILS: '/:orgSlug/hrms/employees/:id',
      ADD: '/:orgSlug/hrms/employees/add',
      EDIT: '/:orgSlug/hrms/employees/:id/edit',
    },
    LEAVE: {
      MAIN: '/:orgSlug/hrms/leave',
      REQUESTS: '/:orgSlug/hrms/leave/requests',
      REQUEST_DETAIL: '/:orgSlug/hrms/leave/requests/:requestUuid',
      APPLY: '/:orgSlug/hrms/leave/apply',
      BALANCE: '/:orgSlug/hrms/leave/balance',
      HISTORY: '/:orgSlug/hrms/leave/history',
    },
    ATTENDANCE: {
      MAIN: '/:orgSlug/hrms/attendance',
      CALENDAR: '/:orgSlug/hrms/calendar',
      REGULARIZATION_REQUESTS: '/:orgSlug/hrms/attendance/regularization-requests',
    },
    MY_ATTENDANCE: '/:orgSlug/hrms/my-attendance',
    MY_LEAVE: '/:orgSlug/hrms/my-leave',
    PROFILE_TRACKER: {
      MAIN: '/:orgSlug/hrms/onboarding-queue',
      REVIEW: '/:orgSlug/hrms/onboarding-queue/:id/review',
    },
    EXIT_MGT: {
      MAIN: '/:orgSlug/hrms/exit-management',
      PROCESS: '/:orgSlug/hrms/exit-management/employee/:id',
    },
    REPOSITORY: {
      MAIN: '/:orgSlug/hrms/repository',
    },
    REPORTS: '/:orgSlug/hrms/reports',
    SETTINGS: {
      MAIN: '/:orgSlug/hrms/settings',
      LEAVE_ALLOCATION: '/:orgSlug/hrms/settings/leave-allocation',
      ATTENDANCE_RULES: '/:orgSlug/hrms/settings/attendance-rules',
      ATTENDANCE_RULES_DETAILS: '/:orgSlug/hrms/settings/attendance-rules/:policyId',
      DEPARTMENTS: '/:orgSlug/hrms/settings/departments',
      DEPARTMENTS_DESIGNATIONS: '/:orgSlug/hrms/settings/departments/:id/designations',
      HOLIDAY_CALENDARS: '/:orgSlug/hrms/settings/holiday-calendars',
      HOLIDAY_CALENDARS_DETAILS: '/:orgSlug/hrms/settings/holiday-calendars/:uuid',
      WORKFLOWS: '/:orgSlug/hrms/settings/workflows',
      WORKFLOWS_NEW: '/:orgSlug/hrms/settings/workflows/new',
      WORKFLOWS_EDIT: '/:orgSlug/hrms/settings/workflows/:workflowId',
    },
  },

  // Project Management (PMO) paths
  PMO: {
    GLOBAL_SEARCH: '/:orgSlug/pmo/search',
    PROJECTS: '/:orgSlug/pmo/projects',
    CREATE_PROJECT: '/:orgSlug/pmo/create',
    EDIT_PROJECT: '/:orgSlug/pmo/edit',
    REPORTS_ANALYTICS: '/:orgSlug/pmo/reports-analytics',
    MY_TASKS: '/:orgSlug/pmo/my-tasks',
    TEAM_INSIGHTS: '/:orgSlug/pmo/team-insights',
    TEAM_INSIGHTS_MEMBER_DETAILS: '/:orgSlug/pmo/team-insights/:employeeId',
    // Under /pmo, like HRMS and CRM keep theirs, so the PMO layout and sidebar
    // stay put. `settings` is a static segment, so it outranks `:projectKey`.
    SETTINGS: {
      MAIN: '/:orgSlug/pmo/settings',
      TAGS: '/:orgSlug/pmo/settings/tags',
      CONVERT_TO_SCRUM: '/:orgSlug/pmo/settings/convert-to-scrum',
      MEMBER_AVAILABILITY: '/:orgSlug/pmo/settings/member-availability',
      WORK_SCHEDULE: '/:orgSlug/pmo/settings/work-schedule',
      HOLIDAY_CALENDARS: '/:orgSlug/pmo/settings/holiday-calendars',
    },
    VIEW_PROJECT: '/:orgSlug/pmo/:projectKey',
    BACKLOG: '/:orgSlug/pmo/:projectKey/backlog',
    BOARD: '/:orgSlug/pmo/:projectKey/board',
    ACTIVE_SPRINT: '/:orgSlug/pmo/:projectKey/active-sprint',
    SPRINT_HISTORY: '/:orgSlug/pmo/:projectKey/sprint-history',
    EPICS: '/:orgSlug/pmo/:projectKey/epics',
    EPIC_DETAILS: '/:orgSlug/pmo/:projectKey/epics/:epicKey',
    CREATE_TASK: '/:orgSlug/pmo/:projectKey/create-task',
    TASK_DETAILS: '/:orgSlug/pmo/:projectKey/:workItemKey',
  },

  // CRM Module paths
  CRM: {
    DASHBOARD: '/:orgSlug/crm/dashboard',
    CONTACTS: {
      VIEW_ALL: '/:orgSlug/crm/contacts',
      DETAILS: '/:orgSlug/crm/contacts/:contactKey',
    },
    CREATE_CONTACT: '/:orgSlug/crm/contacts/create',
    CONTACT_DETAILS: '/:orgSlug/crm/contacts/:contactKey',
    COMPANIES: '/:orgSlug/crm/companies',
    CREATE_COMPANY: '/:orgSlug/crm/companies/create',
    COMPANY_DETAILS: '/:orgSlug/crm/companies/:companyKey',
    DEALS: '/:orgSlug/crm/deals',
    CREATE_DEAL: '/:orgSlug/crm/deals/create',
    DEAL_DETAILS: '/:orgSlug/crm/deals/:dealKey',
    TASKS: '/:orgSlug/crm/tasks',
    CREATE_TASK: '/:orgSlug/crm/tasks/create',
    REPORTS: '/:orgSlug/crm/reports',
    // Under /crm rather than the cross-module /settings prefix, so the CRM
    // layout and its sidebar stay put — the same way HRMS keeps its own.
    SETTINGS: {
      MAIN: '/:orgSlug/crm/settings',
      TAGS: '/:orgSlug/crm/settings/tags',
      VALUES: '/:orgSlug/crm/settings/values',
      CURRENCY: '/:orgSlug/crm/settings/currency',
    },
  },
} as const;

// Narrower than the PMO admin roles on purpose: configuring the module is an
// admin's job, so PMO Manager is left out. Shared so screens that link into
// PMO settings can hide those links from users the route would reject.
export const PMO_SETTINGS_ROLES = ["ORG_OWNER", "ORG_ADMIN", "PMO_ADMIN"];
